import { useEffect, useMemo, useState, type ReactNode } from "react";
import { LocaleContext, type Locale } from "./locale-context";
import { STRINGS, detectLocale, persistLocale } from "./index";
import { createDocumentLocale, titleFor } from "./documentLocale";

const documentLocale = createDocumentLocale((l) => {
  try {
    document.documentElement.lang = l;
    document.title = titleFor(window.location.pathname, STRINGS[l].meta.title);
  } catch {
    /* SSR */
  }
});

/** Re-apply <html lang> and the title for the current route. Called on every
 *  client-side navigation (see RouteDocumentSync in App). */
export function refreshDocumentLocale(): void {
  documentLocale.refresh();
}

/**
 * Owns the active locale. `fixed` pins it (used by the build-time prerender and
 * by the /uz and /ru routes); otherwise it is detected once and can be changed
 * from Settings. Keeps <html lang> in sync for screen readers and search.
 */
export function LocaleProvider({ fixed, children }: { fixed?: Locale; children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => fixed ?? detectLocale());

  useEffect(() => {
    if (fixed && fixed !== locale) setLocaleState(fixed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixed]);

  useEffect(() => {
    if (fixed) return documentLocale.pin(locale);
    documentLocale.setBase(locale);
    return undefined;
  }, [locale, fixed]);

  const value = useMemo(
    () => ({
      locale,
      strings: STRINGS[locale],
      setLocale: (l: Locale) => {
        persistLocale(l);
        setLocaleState(l);
      },
    }),
    [locale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
